import { DialogTitle, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { formatQty, shortfallAgainstFill } from '../utils/bulkLotNdcStatus';

/**
 * The picker's heading: what is being dispensed, and what is wrong with the lot on record.
 *
 * Its own file because ChangeNdcLotDialog sits at the 450-line cap, the same split its sibling
 * panels already use.
 *
 * POM-6119 — a lot short of the fill gets its own sentence. It is on the shelf and in date, and
 * the list underneath offers it, so calling it "no longer usable" made the heading contradict the
 * control directly below it.
 */
const ChangeNdcLotDialogTitle = ({ item, fillQuantity, unusableRecordedLot, shortfallAllowed }) => {
  // Gated on the same flag the lot list is: with the shortfall path off that lot is filtered out
  // of the picker, so calling it merely short would soften a heading for a lot nothing can use.
  const isShort =
    shortfallAllowed &&
    !!unusableRecordedLot &&
    shortfallAgainstFill({ lot: unusableRecordedLot, fillQuantity }) > 0;

  let lotNotice = null;
  if (unusableRecordedLot) {
    lotNotice = isShort
      ? `Reserved ${unusableRecordedLot.lotNumber} holds ${formatQty(
          unusableRecordedLot.remainingQty
        )} of ${formatQty(fillQuantity)}`
      : `Reserved ${unusableRecordedLot.lotNumber} is no longer usable`;
  }

  return (
    <DialogTitle sx={{ pb: 0.5 }}>
      Change NDC &amp; Lot
      <Typography variant='body2' sx={{ fontWeight: 700, mt: 1 }}>
        {item.medicationName}
        {item.strength ? ` ${item.strength}` : ''}
      </Typography>
      <Typography variant='caption' color='text.secondary' component='div'>
        {[item.orderId, item.rxNumber, item.fulfillingPharmacyName].filter(Boolean).join(' · ')}
        {` · Qty ${formatQty(item.fillQuantity)}`}
      </Typography>
      {lotNotice && (
        <Typography variant='caption' color={isShort ? 'warning.dark' : 'error'} component='div'>
          {lotNotice}
        </Typography>
      )}
    </DialogTitle>
  );
};

ChangeNdcLotDialogTitle.propTypes = {
  item: PropTypes.shape({
    medicationName: PropTypes.string,
    strength: PropTypes.string,
    orderId: PropTypes.string,
    rxNumber: PropTypes.string,
    fulfillingPharmacyName: PropTypes.string,
    fillQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  fillQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  unusableRecordedLot: PropTypes.shape({
    lotNumber: PropTypes.string,
    remainingQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  shortfallAllowed: PropTypes.bool,
};

ChangeNdcLotDialogTitle.defaultProps = {
  fillQuantity: 0,
  unusableRecordedLot: null,
  shortfallAllowed: false,
};

export default ChangeNdcLotDialogTitle;
